import {Component, inject, OnInit, signal, DestroyRef} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {PendingUsersService} from "../../../services/pending-users.service";
import {InvitedUsersService} from "../../../services/invited-users.service";
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {SessionService} from "../../../services/session.service";
import {JobsService} from "../../../services/jobs.service";
import {ToastService} from "../../../services/toast.service";
import {INVITED_USERS_TABLE_COLUMNS} from "./invited-users-table.config";
import {TableData} from "../../../interfaces/table/table-data";
import {auditTime, distinctUntilChanged, Subject, switchMap, tap} from "rxjs";
import {TableParams} from "../../../interfaces/table/table-params";
import {ObjectUtils} from "../../../utils/object-utils";

@Component({
  selector: 'app-invited-users',
  templateUrl: './invited-users.component.html',
  styleUrl: './invited-users.component.css',
  standalone: false
})
export class InvitedUsersComponent implements OnInit {
  search: any = '';
  invitationForm!: FormGroup;
  currentUser: any;
  locations: any[] = [];
  roles: any[] = [];
  isLoaded: boolean = false;
  isPopupOpened: boolean = false;
  isInviting: boolean = false;

  tableData: TableData = {data: [], total: 0};
  readonly tableColumns = INVITED_USERS_TABLE_COLUMNS;
  loadingTableData = signal<boolean>(true);
  loadInvitedUsers$ = new Subject<{ tableParams: TableParams, search: string }>();
  tableParams: TableParams = {currentPage: 1, pageSize: 10, sortParams: null};

  private readonly invitedUsersService = inject(InvitedUsersService);
  private readonly fb = inject(FormBuilder);
  private readonly toastService = inject(ToastService);
  private readonly sessionStorage = inject(SessionService);
  private readonly pendingUserService = inject(PendingUsersService);
  private readonly jobService = inject(JobsService);
  private readonly destroyRef = inject(DestroyRef);

  ngOnInit() {
    this.initInvitationForm();
    this.initObservable();

    this.sessionStorage.currentUser.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((user: any) => {
      this.currentUser = user.user;
      this.invitationForm.get('company')?.setValue(user.user.companies);
    });

    this.refreshData();

    Promise.all([
      this.getLocations(),
      this.getRoles()
    ]).then(() => {
      this.isLoaded = true;
    });
  }

  initInvitationForm() {
    this.invitationForm = this.fb.group({
      first_name: ['', Validators.required],
      last_name: ['', Validators.required],
      email: ['',
        [Validators.pattern("^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+"), Validators.required]],
      company: [{value: '', disabled: true}],
      phone: [''],
      role: [1, [Validators.required]],
      locations: [[], [Validators.required]],
      select_all_location: [false],
    });

    const selectAllLocationControl = this.invitationForm.get('select_all_location')!;
    selectAllLocationControl.valueChanges.pipe(
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      if (selectAllLocationControl.value) {
        this.invitationForm.get('locations')?.setValue(this.locations.map((location: any) => location.location_id));
      }
    });
  }

  initObservable() {
    this.loadInvitedUsers$.pipe(
      distinctUntilChanged((prev, curr) => ObjectUtils.deepEqual(prev, curr)),
      tap(_ => this.loadingTableData.set(true)),
      auditTime(0),
      switchMap(params => {
        const {tableParams, search} = params;
        const offset = (tableParams.currentPage - 1) * tableParams.pageSize;
        return this.invitedUsersService.getInvitedUsers(tableParams.pageSize, offset, search, tableParams.sortParams);
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((response: any) => {
      this.loadingTableData.set(false);
      this.tableData = {
        data: response.data.map((item: any) => ({
          cp_pending_user_id: item.cp_invited_user_id,
          first_name: item.first_name,
          last_name: item.last_name,
          work_email: item.work_email,
          phone: item.phone
        })),
        total: response.data.length !== 0 ? response.data[0].total_count : 0,
      };
    });
  }

  async getLocations() {
    await this.jobService.getLocations().then((data: any) => {
      this.locations = data;
    });
  }

  async getRoles() {
    await this.pendingUserService.getRoles().then((data: any) => {
      this.roles = data.Data;
    });
  }

  refreshData() {
    const tableParams = {...this.tableParams}
    this.loadInvitedUsers$.next({tableParams, search: this.search});
  }

  onTableParamChange(params: TableParams) {
    this.tableParams = params;
    this.refreshData();
  }

  togglePopup() {
    this.isPopupOpened = !this.isPopupOpened;
  }

  isInvalidAndTouched(controlName: string): boolean {
    const control = this.invitationForm.get(controlName);
    return control != null && control.invalid && (control.dirty || control.touched);
  }

  inviteUser() {
    this.invitationForm.markAllAsTouched();
    if (this.invitationForm.invalid) return;

    this.isInviting = true;
    const formValues = this.invitationForm.value;
    const locationIds = formValues.locations.map((location_id: any) => ({location_id}));
    const body = {
      firstName: formValues.first_name,
      lastName: formValues.last_name,
      workEmail: formValues.email,
      phone: formValues.phone ?? null,
      companyId: this.currentUser.customer_id,
      locations: JSON.stringify(locationIds),
      originId: "1",
      roleId: formValues.role,
      invitedByCpUserId: this.currentUser.user_id,
      invitedByRamsUserId: null
    }
    this.invitedUsersService.createInvitedUser(body).then((data: any) => {
      if (data.IsSuccess == true) {
        this.toastService.showToast('User successfully invited', 'success');
        this.invitationForm.reset();
        this.refreshData();
      }
    }).catch().finally(() => {
      this.isInviting = false;
      this.isPopupOpened = false;
    });
  }
}
